// SAM-1074 — what the Activity overview draws, for the businesses it lists.
// - "All businesses" as a tenant admin: GET /bookings/stats and GET /business_access/stats once for the
//   whole tenant (by_business gives each business's count) and, for the table, the pass stats of the tenant (its
//   by_business gives each business's passes by status); upcoming bookings per business. If either tenant-wide
//   call is refused or not deployed (403/400, no by_business), that part falls back to one request per business.
// - One business picked, or any other role: per business, booking stats, pass stats and upcoming bookings.
// Each request is cached 2 minutes; a failed one leaves that number out (the page shows a dash).
import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { fetchBookingStats, fetchPassStats, fetchUpcomingBookings, type StatsResponse, type UpcomingBooking } from '../api/activityStats';
import { buildActivityData, passCountsOf, windowRange, type ActivityData, type BusinessInputs, type DayWindow } from '../pages/Bookings/activityOverview';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STALE = 2 * 60 * 1000;
const opts = { staleTime: STALE, retry: false } as const;

export function useActivityData(args: {
  businesses: Array<{ id: string; name: string }>;
  win: DayWindow;
  /** Booking status for the stats filter (`pending`, `no_show`, …), '' for any. */
  status: string;
  now: Date;
  /** "All businesses" is picked: try the tenant-wide stats first. */
  tenantWide?: boolean;
}): { data: ActivityData; isLoading: boolean; updatedAt: number; singleCall: boolean } {
  const { businesses, win, status, now, tenantWide = false } = args;
  const week = useMemo(() => windowRange('week', now), [now]);
  const range = useMemo(() => windowRange(win, now), [win, now]);
  const list = useMemo(() => businesses.filter((b) => UUID_RE.test(String(b.id))), [businesses]);

  // --- tenant-wide stats (one request each) ---
  const tenantWeek = useQuery({
    queryKey: ['activity-tenant-bookings', week.from, week.to, status],
    queryFn: () => fetchBookingStats({ from: week.from, to: week.to, status: status || undefined }),
    enabled: tenantWide, ...opts,
  });
  const tenantWin = useQuery({
    queryKey: ['activity-tenant-bookings', range.from, range.to, status],
    queryFn: () => fetchBookingStats({ from: range.from, to: range.to, status: status || undefined }),
    enabled: tenantWide && win !== 'week', ...opts,
  });
  const tenantPending = useQuery({
    queryKey: ['activity-tenant-bookings', week.from, week.to, 'pending'],
    queryFn: () => fetchBookingStats({ from: week.from, to: week.to, status: 'pending' }),
    enabled: tenantWide && status === '', ...opts,
  });
  const tenantPasses = useQuery({
    queryKey: ['activity-tenant-passes', week.from, week.to],
    queryFn: () => fetchPassStats({ date_from: week.from, date_to: week.to, entity: 'business_access' }),
    enabled: tenantWide, ...opts,
  });

  // Passes of the chosen window per business (the week answer already is the window when it is the week).
  const tenantPassesWin = useQuery({
    queryKey: ['activity-tenant-passes', range.from, range.to],
    queryFn: () => fetchPassStats({ date_from: range.from, date_to: range.to, entity: 'business_access' }),
    enabled: tenantWide && win !== 'week', ...opts,
  });
  const passesAnswer = win === 'week' ? tenantPasses : tenantPassesWin;
  const singlePasses = tenantWide && passesAnswer.isSuccess && Array.isArray(passesAnswer.data?.by_business);
  const passesSettled = !tenantWide || passesAnswer.isSuccess || passesAnswer.isError;
  const perBusinessPasses = passesSettled && !singlePasses;

  const tenantReady = tenantWide && tenantWeek.isSuccess && Array.isArray(tenantWeek.data?.by_business);
  const settled = !tenantWide || tenantWeek.isSuccess || tenantWeek.isError;
  const singleCall = tenantReady;
  const perBusinessBookings = settled && !singleCall;

  // --- per business ---
  const queries = useQueries({
    queries: list.flatMap((b) => [
      {
        queryKey: ['activity-booking-stats', b.id, week.from, week.to, status],
        queryFn: () => fetchBookingStats({ business_id: b.id, from: week.from, to: week.to, status: status || undefined }),
        enabled: perBusinessBookings, ...opts,
      },
      {
        queryKey: ['activity-pass-stats', b.id, week.from, week.to],
        queryFn: () => fetchPassStats({ business_id: b.id, date_from: week.from, date_to: week.to, entity: 'business_access' }),
        enabled: perBusinessPasses, ...opts,
      },
      {
        queryKey: ['activity-upcoming', b.id, week.from, week.to],
        queryFn: () => fetchUpcomingBookings({ business_id: b.id, from: week.from, to: week.to }),
        ...opts,
      },
    ]),
  });

  const countOf = (resp: { by_business?: Array<{ business_id: string; count: number }> } | undefined, id: string): number | null =>
    resp?.by_business ? resp.by_business.find((x) => String(x.business_id) === id)?.count ?? 0 : null;

  const inputs: BusinessInputs[] = list.map((b, i) => {
    const id = String(b.id);
    const passRow = singlePasses ? passesAnswer.data?.by_business?.find((x) => String(x.business_id) === id) : undefined;
    const base = {
      id, name: b.name, upcoming: queries[i * 3 + 2]?.data as UpcomingBooking[] | undefined,
      ...(singlePasses ? { passCounts: passCountsOf(passRow) } : { passes: queries[i * 3 + 1]?.data as StatsResponse | undefined }),
    };
    if (!singleCall) return { ...base, bookings: queries[i * 3]?.data as StatsResponse | undefined };
    const weekCount = countOf(tenantWeek.data, id);
    return {
      ...base,
      counts: {
        window: win === 'week' ? weekCount : countOf(tenantWin.data, id),
        week: weekCount,
        pending: status === '' ? countOf(tenantPending.data, id) : status === 'pending' ? weekCount : 0,
      },
    };
  });

  const data = buildActivityData(inputs, win, now, {
    ...(singleCall ? { bookings: tenantWeek.data } : {}),
    ...(tenantWide && tenantPasses.isSuccess ? { passes: tenantPasses.data } : {}),
  });
  // The status of the window and the tile counts come from the tenant answer for the chosen window.
  const queriesLoading = queries.some((q) => q.isLoading) || (tenantWide && (tenantWeek.isLoading || passesAnswer.isLoading));
  const updatedAt = Math.max(0, tenantWeek.dataUpdatedAt || 0, ...queries.map((q) => q.dataUpdatedAt || 0));
  return { data, isLoading: queriesLoading, updatedAt, singleCall };
}
